import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ClipImporter } from '@/components/clips/ClipImporter'
import { ClipRelationships } from '@/components/clips/ClipRelationships'
import { ClipCommitAssociations } from '@/components/clips/ClipCommitAssociations'
import { ClipDetailContent } from '@/components/clips/ClipDetailContent'
import { ColumnPicker, getMetadataDisplayName } from '@/components/clips/ColumnPicker'
import { EntityDetailSheet } from '@/components/shared/EntityDetailSheet'
import { LinkRelationshipDialog } from '@/components/relationships/LinkRelationshipDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useClips, useDeleteClips, useBulkUpdateClipTags, useBulkUpdateClipStatus } from '@/hooks/useClips'
import { useVfxShots } from '@/hooks/useVfxShots'
import { useClipSelection } from '@/hooks/useClipSelection'
import { useEntitySearch } from '@/hooks/useEntitySearch'
import { useEntityEdit } from '@/hooks/useEntityEdit'
import { Upload, Film, Search } from 'lucide-react'
import { useDeleteClip, useUpdateClip } from '@/hooks/useClips'
import { useTags, useUpdateClipTags } from '@/hooks/useTags'
import { createClipRelationship } from '@/lib/api/clipRelationships'
import { updateVfxShot } from '@/lib/api/vfxShots'
import { BulkActionBar } from '@/components/shared/BulkActionBar'
import { TagsDialog } from '@/components/shared/TagsDialog'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { StatusSelector } from '@/components/shared/StatusSelector'
import { EntityRowActions } from '@/components/shared/EntityRowActions'
import { ExpandableRowButton } from '@/components/shared/ExpandableRowButton'
import { TagsDisplayCell } from '@/components/shared/TagsDisplayCell'
import { MetadataCountBadge } from '@/components/shared/MetadataCountBadge'
import { SelectableTableRow } from '@/components/shared/SelectableTableRow'
import { ExpandedMetadataRow } from '@/components/shared/ExpandedMetadataRow'
import { toast } from 'sonner'
import type { Project, Clip } from '@/types/database'
import type { ClipStatusType } from '@/types/status'
import { formatDuration } from '@/lib/utils'
import { usePermissions } from '@/hooks/usePermissions'
import { useAnalytics } from '@/hooks/useAnalytics'

interface ClipViewConfig {
  visibleMetadataColumns: string[]
}

interface ProjectClipsTabProps {
  project: Project
}

export function ProjectClipsTab({ project }: ProjectClipsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: clips = [], isLoading: clipsLoading, error } = useClips(project.id)
  const { data: vfxShotsData } = useVfxShots(project.id)
  const { data: tags = [] } = useTags(project.id)
  const deleteClip = useDeleteClip()
  const updateClipMutation = useUpdateClip()
  const updateClipTags = useUpdateClipTags()
  const permissions = usePermissions(project.id, project.owner_user_id)
  const { track } = useAnalytics()

  const vfxShots = vfxShotsData?.shots || []

  // Debounced search tracking to avoid excessive events on every keystroke
  const searchTrackingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const trackSearchDebounced = useCallback((query: string) => {
    // Clear previous timeout
    if (searchTrackingTimeoutRef.current !== undefined) {
      clearTimeout(searchTrackingTimeoutRef.current)
    }

    // Set new timeout to track after 1 second of no typing
    searchTrackingTimeoutRef.current = setTimeout(() => {
      track('clips_searched', {
        project_id: project.id,
        query_length: query.length,
        has_field_filter: query.includes(':'),
      })
    }, 1000)
  }, [project.id, track])

  // Bulk operations mutations
  const bulkDelete = useDeleteClips()
  const bulkUpdateTags = useBulkUpdateClipTags()
  const bulkUpdateStatus = useBulkUpdateClipStatus()

  // UI State
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [expandedClipId, setExpandedClipId] = useState<string | null>(null)
  const [selectedMetadataColumns, setSelectedMetadataColumns] = useState<string[]>([])
  const [relationshipsDialogOpen, setRelationshipsDialogOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null)
  const [tagsDialogMode, setTagsDialogMode] = useState<'single' | 'bulk' | null>(null)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)
  const [detailClipId, setDetailClipId] = useState<string | null>(null)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [bulkStatus, setBulkStatus] = useState<string | null>(null)
  const [bulkStatusType, setBulkStatusType] = useState<ClipStatusType | null>(null)
  const [sortBy, setSortBy] = useState<'default' | 'tag' | 'name'>('default')

  // Check for clip query parameter and open detail if present
  useEffect(() => {
    const clipParam = searchParams.get('clip')
    if (clipParam && clips) {
      const clip = clips.find(c => c.id === clipParam)
      if (clip) {
        setDetailClipId(clipParam)
        setDetailSheetOpen(true)
        // Remove the query parameter from URL
        searchParams.delete('clip')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [searchParams, clips, setSearchParams])

  // Shared hooks for state management
  const editState = useEntityEdit<Clip>()

  // Search and filter with shared hook - now supports column-specific syntax
  // Field map for column-specific search (e.g., "scene:15", "tape:A001")
  const fieldMap = useMemo(() => {
    const map: Record<string, number> = {
      'name': 0,
      'tape': 1,
      'tape name': 1,
      'timecode': 2,
      'tc': 2,
      'source': 2,
      'path': 3,
      'file': 3,
    }

    // Add custom metadata fields to the map dynamically
    if (clips && clips.length > 0) {
      const customFields = new Set<string>()
      clips.forEach(clip => {
        if (clip.custom_metadata) {
          Object.keys(clip.custom_metadata).forEach(key => customFields.add(key))
        }
      })

      // Map custom fields (starting after core fields + tags)
      // IMPORTANT: Sort fields alphabetically to ensure consistent ordering
      const sortedFields = Array.from(customFields).sort()
      const tagsLength = clips[0]?.tags?.length || 0
      sortedFields.forEach((field, index) => {
        const normalizedField = field.toLowerCase()
        const fieldIndex = 4 + tagsLength + index
        map[normalizedField] = fieldIndex
      })
    }

    return map
  }, [clips])

  // Specify fields that should use exact matching (e.g., scene numbers)
  const exactMatchFields = useMemo(() => new Set(['scene']), [])

  // Get all custom field keys sorted alphabetically (to match fieldMap)
  const allCustomFieldKeys = useMemo(() => {
    const customFields = new Set<string>()
    clips?.forEach(clip => {
      if (clip.custom_metadata) {
        Object.keys(clip.custom_metadata).forEach(key => customFields.add(key))
      }
    })
    return Array.from(customFields).sort()
  }, [clips])

  const { searchQuery, setSearchQuery, filteredItems: filteredClips } = useEntitySearch(
    clips || [],
    (clip) => [
      clip.name,
      clip.tape_name,
      clip.source_timecode,
      clip.source_file_path,
      ...(clip.tags || []),
      // IMPORTANT: Include ALL custom fields in consistent order, using undefined for missing values
      // This ensures field indices match across all clips
      ...allCustomFieldKeys.map(key => {
        const value = clip.custom_metadata?.[key]
        return value !== undefined && value !== null
          ? (typeof value === 'string' ? value : JSON.stringify(value))
          : undefined
      })
    ],
    fieldMap,
    exactMatchFields
  )

  // Sort and group clips based on sortBy selection
  const sortedAndGroupedClips = useMemo(() => {
    if (sortBy === 'default') {
      return [{ tag: null, items: filteredClips }]
    }

    const sorted = [...filteredClips].sort((a, b) => {
      if (sortBy === 'tag') {
        const aTag = a.tags?.[0] || 'Untagged'
        const bTag = b.tags?.[0] || 'Untagged'
        return aTag.localeCompare(bTag)
      } else if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '')
      }
      return 0
    })

    if (sortBy === 'tag') {
      // Group by tag combination (all tags together)
      const grouped = new Map<string, Clip[]>()
      sorted.forEach(clip => {
        const tags = clip.tags || []
        // Create a key from all tags joined together
        const tagKey = tags.length > 0 ? tags.join(', ') : 'Untagged'
        if (!grouped.has(tagKey)) {
          grouped.set(tagKey, [])
        }
        grouped.get(tagKey)!.push(clip)
      })

      // Convert to array and sort tag names alphabetically
      return Array.from(grouped.entries())
        .sort(([a], [b]) => {
          // Put "Untagged" last
          if (a === 'Untagged') return 1
          if (b === 'Untagged') return -1
          return a.localeCompare(b)
        })
        .map(([tag, items]) => ({ tag, items }))
    }

    return [{ tag: null, items: sorted }]
  }, [filteredClips, sortBy])

  // Flatten clips for selection (needed for multi-select)
  const flattenedClips = useMemo(() => {
    return sortedAndGroupedClips.flatMap(group => group.items)
  }, [sortedAndGroupedClips])

  // Virtual scrolling setup
  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: flattenedClips.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside visible area
  })

  // Multi-select state
  const selection = useClipSelection(clips || [], flattenedClips)

  // Extract all unique field names from clips - both core fields and custom metadata
  const availableMetadataFields = useMemo(() => {
    if (!clips || clips.length === 0) return []

    const fieldsSet = new Set<string>()

    // Add core fields that are always available
    fieldsSet.add('Name')
    fieldsSet.add('Tape')
    fieldsSet.add('Start')
    fieldsSet.add('Duration')

    // Add all custom metadata fields
    clips.forEach(clip => {
      if (clip.custom_metadata) {
        Object.keys(clip.custom_metadata).forEach(key => {
          // Skip duplicates that match core field mappings
          // Note: Start and End are kept in metadata for reference, but Start column uses source_timecode
          if (key !== 'Name' && key !== 'Tape' && key !== 'Duration') {
            fieldsSet.add(key)
          }
        })
      }
    })

    return Array.from(fieldsSet).sort()
  }, [clips])

  // Load view config from localStorage on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem(`clipView_v2_${project.id}`)

    if (savedConfig) {
      try {
        const config: ClipViewConfig = JSON.parse(savedConfig)
        const savedColumns = config.visibleMetadataColumns || []
        setSelectedMetadataColumns(savedColumns)
      } catch (error) {
        console.error('Failed to load view config:', error)
        setSelectedMetadataColumns([])
      }
    } else {
      // No saved config - start with empty selection
      setSelectedMetadataColumns([])
    }
  }, [project.id])

  // Check if any clip has status set (to determine if status column should be shown)
  const hasAnyStatus = useMemo(() => {
    return clips?.some(c => c.status) || false
  }, [clips])

  // Helper functions for bulk tag operations
  const getCommonTags = (clips: Clip[]) => {
    if (clips.length === 0) return []
    const tagSets = clips.map(c => new Set(c.tags || []))
    return Array.from(tagSets[0]).filter(tag =>
      tagSets.every(set => set.has(tag))
    )
  }

  const getPartialTags = (clips: Clip[]) => {
    const allTags = new Set(clips.flatMap(c => c.tags || []))
    const commonTags = new Set(getCommonTags(clips))
    return Array.from(allTags).filter(tag => !commonTags.has(tag))
  }

  const handleDeleteClip = async (clipId: string, clipName: string) => {
    if (window.confirm('Are you sure you want to delete this clip?')) {
      try {
        await deleteClip.mutateAsync({ clipId, projectId: project.id, clipName })

        // Track clip deletion (no clip_name for privacy - contains scene/shot details)
        track('clip_deleted', {
          project_id: project.id,
          clip_id: clipId,
        })

        toast.success('Clip deleted successfully')
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete clip')
      }
    }
  }

  // Commented out for now - relationships dialog not yet fully implemented
  // const handleViewRelationships = (_clip: Clip) => {
  //   setSelectedClip(clip)
  //   setRelationshipsDialogOpen(true)
  // }

  const handleLinkClip = (clip: Clip) => {
    setSelectedClip(clip)
    setLinkDialogOpen(true)
  }

  const handleLinkClipToTarget = async (targetClipId: string, relationshipType: string = 'related') => {
    if (!selectedClip) return
    try {
      await createClipRelationship(selectedClip.id, {
        target_clip_id: targetClipId,
        relationship_type: relationshipType,
        metadata: {}
      })

      // Track clips linked
      track('clips_linked', {
        project_id: project.id,
        source_clip_id: selectedClip.id,
        target_clip_id: targetClipId,
        entity_type: 'clip',
        relationship_type: relationshipType,
      })

      toast.success('Clip linked successfully')
    } catch (error: any) {
      console.error('Failed to link clip:', error)
      toast.error(error.message || 'Failed to link clip')
      throw error
    }
  }

  const handleLinkClipToVfxShot = async (vfxShotId: string) => {
    if (!selectedClip) return
    try {
      await updateVfxShot(vfxShotId, { clip_id: selectedClip.id })

      // Track clip linked to VFX shot
      track('clips_linked', {
        project_id: project.id,
        source_clip_id: selectedClip.id,
        target_id: vfxShotId,
        entity_type: 'vfx_shot',
      })

      toast.success('VFX shot linked successfully')
    } catch (error: any) {
      console.error('Failed to link VFX shot:', error)
      toast.error(error.message || 'Failed to link VFX shot')
      throw error
    }
  }

  const handleViewClipDetails = (clip: Clip) => {
    // Track clip viewed (no clip_name for privacy - contains scene/shot details)
    track('clip_viewed', {
      project_id: project.id,
      clip_id: clip.id,
    })

    setDetailClipId(clip.id)
    setDetailSheetOpen(true)
  }

  // Single clip tag editing
  const handleEditTags = (clip: Clip) => {
    setSelectedClip(clip)
    setTagsDialogMode('single')
  }

  const handleSingleSaveTags = async (newTags: string[]) => {
    if (!selectedClip) return

    try {
      await updateClipTags.mutateAsync({
        clipId: selectedClip.id,
        tags: newTags,
      })

      // Track clip tags edited (no clip_name for privacy - contains scene/shot details)
      track('clip_tags_edited', {
        project_id: project.id,
        clip_id: selectedClip.id,
        tags_count: newTags.length,
        tags_added_count: newTags.filter(t => !selectedClip.tags?.includes(t)).length,
        tags_removed_count: (selectedClip.tags?.filter(t => !newTags.includes(t)) || []).length,
      })

      toast.success('Tags updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update tags')
    }
  }

  // Bulk operations handlers
  const handleBulkEditTags = () => {
    setTagsDialogMode('bulk')
  }

  const handleBulkSaveTags = async (newTags: string[]) => {
    const currentCommonTags = getCommonTags(selection.selectedItems)
    const addTags = newTags.filter(t => !currentCommonTags.includes(t))
    const removeTags = currentCommonTags.filter(t => !newTags.includes(t))

    try {
      await bulkUpdateTags.mutateAsync({
        projectId: project.id,
        clipIds: Array.from(selection.selectedIds),
        addTags: addTags.length > 0 ? addTags : undefined,
        removeTags: removeTags.length > 0 ? removeTags : undefined
      })
      selection.clearSelection()
    } catch (error: any) {
      toast.error(error.message || 'Failed to update tags')
    }
  }

  const handleBulkDelete = () => {
    if (!window.confirm(`Delete ${selection.selectedCount} clip${selection.selectedCount !== 1 ? 's' : ''}?`)) {
      return
    }

    bulkDelete.mutate({
      projectId: project.id,
      clipIds: Array.from(selection.selectedIds)
    }, {
      onSuccess: () => {
        toast.success(`${selection.selectedCount} clip${selection.selectedCount !== 1 ? 's' : ''} deleted`)
        selection.clearSelection()
      }
    })
  }

  const handleBulkStatusUpdate = () => {
    if (!bulkStatus || !bulkStatusType) {
      toast.error('Please select status type and status')
      return
    }

    bulkUpdateStatus.mutate({
      projectId: project.id,
      clipIds: Array.from(selection.selectedIds),
      status: bulkStatus,
      statusType: bulkStatusType
    }, {
      onSuccess: () => {
        toast.success('Status updated successfully')
        setStatusDialogOpen(false)
        setBulkStatus(null)
        setBulkStatusType(null)
        selection.clearSelection()
      }
    })
  }

  // Edit mode handlers (using shared hook)
  const handleStartEdit = (clip: Clip) => {
    editState.startEdit(clip)
    // Auto-expand to show all metadata when editing
    setExpandedClipId(clip.id)
  }

  const handleCancelEdit = () => {
    editState.cancelEdit()
    setExpandedClipId(null) // Collapse when canceling
  }

  const handleSaveEdit = async () => {
    if (!editState.editingId || !editState.editedData) return

    try {
      await updateClipMutation.mutateAsync({
        id: editState.editingId,
        name: editState.editedData.name,
        tape_name: editState.editedData.tape_name,
        source_timecode: editState.editedData.source_timecode,
        duration_frames: editState.editedData.duration_frames,
        fps: editState.editedData.fps,
        source_file_path: editState.editedData.source_file_path,
        custom_metadata: editState.editedData.custom_metadata,
        tags: editState.editedData.tags,
        status: editState.editedData.status,
        status_type: editState.editedData.status_type,
      })

      // Track clip metadata edited (no clip_name for privacy - contains scene/shot details)
      track('clip_metadata_edited', {
        project_id: project.id,
        clip_id: editState.editingId,
      })

      toast.success('Clip updated successfully')
      editState.clearEdit()
      setExpandedClipId(null) // Collapse after saving
    } catch (error: any) {
      console.error('Failed to update clip:', error)
      toast.error(error.message || 'Failed to update clip')
    }
  }

  const handleFieldChange = (field: keyof Clip, value: any) => {
    editState.updateField(field, value)
  }

  const handleCustomMetadataChange = (key: string, value: string) => {
    editState.updateField('custom_metadata', {
      ...(editState.editedData.custom_metadata || {}),
      [key]: value
    })
  }

  // Helper to get field value for any column
  const getFieldValue = (clip: Clip, field: string): string => {
    switch (field) {
      case 'Name':
        return clip.name || '-'
      case 'Tape':
        return clip.tape_name || '-'
      case 'Start':
        // source_timecode now contains the Start timecode (synced from custom_metadata.Start)
        return clip.source_timecode || '-'
      case 'Duration':
        return formatDuration(clip.duration_frames, clip.fps)
      default:
        return clip.custom_metadata?.[field] ? String(clip.custom_metadata[field]) : '-'
    }
  }

  return (
    <div className="space-y-6">
      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selection.selectedCount}
        onClearSelection={selection.clearSelection}
        onAddTags={handleBulkEditTags}
        onSetStatus={() => setStatusDialogOpen(true)}
        onDelete={handleBulkDelete}
        entityType="clip"
      />

      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          {clips && clips.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search clips... Try: scene:15 (exact), tape:A001, or just 15"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  // Track search with debouncing (only when query has content)
                  if (e.target.value.trim()) {
                    trackSearchDebounced(e.target.value)
                  }
                }}
                className="pl-9"
              />
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {/* Sort By Dropdown */}
          {clips && clips.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Sort by:</label>
              <Select value={sortBy} onValueChange={(value: any) => {
                setSortBy(value)
                // Track sort change
                track('clips_sorted', {
                  project_id: project.id,
                  sort_by: value,
                })
              }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="tag">Tag</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {availableMetadataFields.length > 0 && (
            <ColumnPicker
              projectId={project.id}
              availableFields={availableMetadataFields}
              selectedFields={selectedMetadataColumns}
              onSelectionChange={setSelectedMetadataColumns}
            />
          )}
          {permissions.canImportFiles && (
            <Button onClick={() => setImportDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import Clips
            </Button>
          )}
        </div>
      </div>

      {/* Search Results Info */}
      {clips && clips.length > 0 && flattenedClips.length !== clips.length && (
        <p className="text-sm text-muted-foreground">
          Showing {flattenedClips.length} of {clips.length} clips
        </p>
      )}
      {clips && clips.length > 0 && flattenedClips.length === clips.length && clips.length > 100 && (
        <p className="text-sm text-muted-foreground">
          {clips.length} clips (virtualized scrolling)
        </p>
      )}

      {/* Loading State */}
      {clipsLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading clips...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive text-center">
              Failed to load clips. Please try again.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!clipsLoading && !error && clips?.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Film className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No clips yet</h3>
              <p className="text-muted-foreground mb-6">
                {permissions.canImportFiles
                  ? 'Get started by importing an ALE file with your clips'
                  : 'No clips have been imported yet'}
              </p>
              {permissions.canImportFiles && (
                <Button onClick={() => setImportDialogOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Import Your First Clips
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clips Table */}
      {!clipsLoading && !error && flattenedClips.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div
              ref={parentRef}
              className="overflow-auto"
              style={{ height: '600px' }}
            >
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-background">
                  <tr className="border-b">
                    {/* Checkbox column */}
                    <th className="px-4 py-3" style={{ width: '50px' }}>
                      <Checkbox
                        checked={selection.isAllSelected}
                        indeterminate={selection.isSomeSelected}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                    {/* Expand column */}
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground" style={{ width: '50px' }}>
                    </th>
                    {/* All columns based on user selection */}
                    {selectedMetadataColumns.map((field) => (
                      <th key={field} className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        {getMetadataDisplayName(field)}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground" style={{ minWidth: '150px' }}>
                      Tags
                    </th>
                    {/* Status column - only show if any clip has status */}
                    {hasAnyStatus && (
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground" style={{ minWidth: '120px' }}>
                        Status
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground" style={{ minWidth: '80px' }}>
                      Metadata
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground" style={{ minWidth: '200px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rowVirtualizer.getVirtualItems().length > 0 && (
                    <tr style={{ height: `${rowVirtualizer.getVirtualItems()[0]?.start || 0}px` }} />
                  )}
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const clip = flattenedClips[virtualRow.index]
                    const index = virtualRow.index
                    // All clips have metadata to show (at minimum: name, tape_name, etc.)
                    const hasAdditionalMetadata = true
                    const isExpanded = expandedClipId === clip.id
                    const isEditing = editState.isEditing(clip.id)
                    const displayClip = isEditing ? editState.editedData : clip

                    return (
                      <React.Fragment key={virtualRow.key}>
                        <SelectableTableRow
                          isSelected={selection.selectedIds.has(clip.id)}
                          isExpanded={isExpanded}
                          isEditing={isEditing}
                          onClick={() => !isEditing && handleViewClipDetails(clip)}
                          index={virtualRow.index}
                        >
                          {/* Checkbox column */}
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selection.selectedIds.has(clip.id)}
                              onCheckedChange={() => selection.toggleSelection(clip.id)}
                            />
                          </td>
                          {/* Expand button column */}
                          <td className="px-4 py-3">
                            <ExpandableRowButton
                              isExpanded={isExpanded}
                              disabled={isEditing}
                              onToggle={(e) => {
                                e.stopPropagation()
                                setExpandedClipId(isExpanded ? null : clip.id)
                              }}
                            />
                          </td>
                          {/* All columns based on user selection */}
                          {selectedMetadataColumns.map((field) => (
                            <td key={field} className="px-4 py-3 text-sm text-muted-foreground">
                              {!isEditing && getFieldValue(clip, field)}
                              {isEditing && field === 'Name' && (
                                <Input
                                  value={displayClip.name || ''}
                                  onChange={(e) => handleFieldChange('name', e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="Clip name"
                                />
                              )}
                              {isEditing && field === 'Tape' && (
                                <Input
                                  value={displayClip.tape_name || ''}
                                  onChange={(e) => handleFieldChange('tape_name', e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="Tape name"
                                />
                              )}
                              {isEditing && field === 'Start' && (
                                <Input
                                  value={displayClip.source_timecode || ''}
                                  onChange={(e) => handleFieldChange('source_timecode', e.target.value)}
                                  className="h-8 text-sm font-mono"
                                  placeholder="00:00:00:00"
                                />
                              )}
                              {isEditing && field === 'Duration' && (
                                <Input
                                  type="number"
                                  value={displayClip.duration_frames || ''}
                                  onChange={(e) => handleFieldChange('duration_frames', parseInt(e.target.value) || undefined)}
                                  className="h-8 text-sm"
                                  placeholder="0"
                                />
                              )}
                              {isEditing && field !== 'Name' && field !== 'Tape' && field !== 'Start' && field !== 'Duration' && (
                                <Input
                                  value={displayClip.custom_metadata?.[field]?.toString() || ''}
                                  onChange={(e) => handleCustomMetadataChange(field, e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder={field}
                                />
                              )}
                            </td>
                          ))}
                          {/* Tags column */}
                          <td className="px-4 py-3 text-sm">
                            <TagsDisplayCell tags={clip.tags || []} maxVisible={2} />
                          </td>
                          {/* Status column - conditional */}
                          {hasAnyStatus && (
                            <td className="px-4 py-3">
                              {clip.status && clip.status_type && (
                                <StatusBadge
                                  status={clip.status}
                                  statusType={clip.status_type}
                                  size="sm"
                                />
                              )}
                            </td>
                          )}
                          {/* Metadata count column */}
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            <MetadataCountBadge
                              entity={clip}
                              coreFields={['name', 'tape_name', 'source_timecode', 'duration_frames', 'fps', 'source_file_path', 'thumbnail_path']}
                            />
                          </td>
                          {/* Actions column */}
                          <td className="px-4 py-3 text-sm text-right">
                            <EntityRowActions
                              isEditing={isEditing}
                              onEdit={() => handleStartEdit(clip)}
                              onTags={() => handleEditTags(clip)}
                              onLink={() => handleLinkClip(clip)}
                              onDelete={() => handleDeleteClip(clip.id, clip.name)}
                              onSave={handleSaveEdit}
                              onCancel={handleCancelEdit}
                              canEdit={permissions.canEditClips && permissions.canDeleteClips}
                              canLink={permissions.canLinkClips}
                              isSaving={updateClipMutation.isPending}
                            />
                          </td>
                        </SelectableTableRow>
                        {/* Expanded metadata row - View mode */}
                        <ExpandedMetadataRow
                          colSpan={6 + selectedMetadataColumns.length + (hasAnyStatus ? 1 : 0)}
                          isExpanded={isExpanded && !isEditing}
                          metadata={clip}
                        />

                        {/* Expanded metadata row - Edit mode */}
                        {isEditing && hasAdditionalMetadata && (
                          <tr
                            key={`${clip.id}-expanded-edit`}
                            className={`
                              border-b
                              ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}
                              bg-blue-50/50 dark:bg-blue-950/20
                            `}
                          >
                            <td colSpan={6 + selectedMetadataColumns.length + (hasAnyStatus ? 1 : 0)} className="px-4 py-3">
                              <div className="ml-10 p-4 bg-muted/30 rounded-md border border-border">
                                <h4 className="text-sm font-semibold mb-3 text-muted-foreground">
                                  Edit Metadata
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                  {/* Standard clip fields */}
                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Name
                                    </div>
                                    <Input
                                      value={displayClip.name || ''}
                                      onChange={(e) => handleFieldChange('name', e.target.value)}
                                      className="h-8 text-sm"
                                      placeholder="Clip name"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Tape Name
                                    </div>
                                    <Input
                                      value={displayClip.tape_name || ''}
                                      onChange={(e) => handleFieldChange('tape_name', e.target.value)}
                                      className="h-8 text-sm"
                                      placeholder="Tape name"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Start
                                    </div>
                                    <Input
                                      value={displayClip.source_timecode || ''}
                                      onChange={(e) => handleFieldChange('source_timecode', e.target.value)}
                                      className="h-8 text-sm font-mono"
                                      placeholder="00:00:00:00"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Duration (frames)
                                    </div>
                                    <Input
                                      type="number"
                                      value={displayClip.duration_frames || ''}
                                      onChange={(e) => handleFieldChange('duration_frames', parseInt(e.target.value) || undefined)}
                                      className="h-8 text-sm"
                                      placeholder="0"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      FPS
                                    </div>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={displayClip.fps || ''}
                                      onChange={(e) => handleFieldChange('fps', parseFloat(e.target.value) || undefined)}
                                      className="h-8 text-sm"
                                      placeholder="24"
                                    />
                                  </div>

                                  <div className="space-y-1 col-span-full">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Source File Path
                                    </div>
                                    <Input
                                      value={displayClip.source_file_path || ''}
                                      onChange={(e) => handleFieldChange('source_file_path', e.target.value)}
                                      className="h-8 text-sm font-mono"
                                      placeholder="Enter source file path"
                                    />
                                  </div>

                                  <div className="space-y-1 col-span-full">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Thumbnail Path
                                    </div>
                                    <Input
                                      value={displayClip.thumbnail_path || ''}
                                      onChange={(e) => handleFieldChange('thumbnail_path', e.target.value)}
                                      className="h-8 text-sm font-mono"
                                      placeholder="Enter thumbnail path"
                                    />
                                  </div>

                                  {/* Custom metadata */}
                                  {clip.custom_metadata && Object.entries(clip.custom_metadata).map(([key]) => (
                                    <div key={key} className="space-y-1">
                                      <div className="text-xs font-medium text-muted-foreground uppercase">
                                        {key}
                                      </div>
                                      <Input
                                        value={displayClip.custom_metadata?.[key]?.toString() || ''}
                                        onChange={(e) => handleCustomMetadataChange(key, e.target.value)}
                                        className="h-8 text-sm font-mono"
                                        placeholder={`Enter ${key}`}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                  {rowVirtualizer.getVirtualItems().length > 0 && (
                    <tr style={{
                      height: `${
                        rowVirtualizer.getTotalSize() -
                        (rowVirtualizer.getVirtualItems()[rowVirtualizer.getVirtualItems().length - 1]?.start || 0) -
                        (rowVirtualizer.getVirtualItems()[rowVirtualizer.getVirtualItems().length - 1]?.size || 0)
                      }px`
                    }} />
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Search Results */}
      {!clipsLoading && !error && clips && clips.length > 0 && flattenedClips.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No clips found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search query
              </p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Dialog */}
      <ClipImporter
        projectId={project.id}
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportComplete={() => {
          // Clips will be automatically refetched due to query invalidation
        }}
      />

      {/* Clip Relationships Dialog */}
      <Dialog open={relationshipsDialogOpen} onOpenChange={setRelationshipsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Clip Relationships & Commits</DialogTitle>
            <DialogDescription>
              {selectedClip ? (
                <>
                  View relationships and commits for <span className="font-semibold">{selectedClip.name}</span>
                </>
              ) : (
                'View relationships and commits for this clip'
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedClip && clips && (
            <div className="space-y-6">
              <ClipCommitAssociations clip={selectedClip} />
              <ClipRelationships
                clip={selectedClip}
                availableClips={clips.filter(c => c.id !== selectedClip.id)}
                allVfxShots={vfxShots}
                allTags={tags.map(t => t.name)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tags Dialog - Supports both single and bulk mode */}
      <TagsDialog
        open={tagsDialogMode !== null}
        onOpenChange={(open) => !open && setTagsDialogMode(null)}
        currentTags={tagsDialogMode === 'single' ? (selectedClip?.tags || []) : []}
        commonTags={tagsDialogMode === 'bulk' ? getCommonTags(selection.selectedItems) : undefined}
        partialTags={tagsDialogMode === 'bulk' ? getPartialTags(selection.selectedItems) : undefined}
        availableTags={tags}
        onSave={tagsDialogMode === 'bulk' ? handleBulkSaveTags : handleSingleSaveTags}
        isBulkMode={tagsDialogMode === 'bulk'}
        entityName={tagsDialogMode === 'single' ? selectedClip?.name : undefined}
        selectedCount={selection.selectedCount}
      />

      {/* Bulk Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Status for {selection.selectedCount} Clip{selection.selectedCount !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Choose status type and value. This will apply to all selected clips.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <StatusSelector
              value={bulkStatus}
              statusType={bulkStatusType}
              onChange={(status, type) => {
                setBulkStatus(status)
                setBulkStatusType(type)
              }}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkStatusUpdate} disabled={!bulkStatus || !bulkStatusType}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clip Detail Sidebar */}
      {detailClipId && (
        <EntityDetailSheet
          open={detailSheetOpen}
          onOpenChange={setDetailSheetOpen}
          title="Clip Details"
          width="xl"
        >
          <ClipDetailContent
            clipId={detailClipId}
            projectId={project.id}
            availableClips={clips || []}
          />
        </EntityDetailSheet>
      )}

      {/* Link Relationship Dialog */}
      {selectedClip && (
        <LinkRelationshipDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          sourceType="clip"
          sourceId={selectedClip.id}
          sourceName={selectedClip.name}
          clips={clips || []}
          vfxShots={vfxShots}
          allTags={tags.map(t => t.name)}
          onLinkClip={handleLinkClipToTarget}
          onLinkVfxShot={handleLinkClipToVfxShot}
        />
      )}
    </div>
  )
}
